class OlyndraSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('.ol-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.ol-hero__dot'));
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval || 6) * 1000;
    this.timer = null;
  }

  connectedCallback() {
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  show(index, restart = false) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);
    });
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    if (restart) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stop();
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('olyndra-slideshow')) {
  customElements.define('olyndra-slideshow', OlyndraSlideshow);
}

document.addEventListener('click', (event) => {
  const control = event.target.closest('[data-ol-slider-direction]');
  if (!control) return;
  const section = control.closest('.collection, .collection-list-wrapper');
  const direction = control.dataset.olSliderDirection === 'previous' ? 'prev' : 'next';
  section?.querySelector(`slider-component .ol-native-slider-buttons .slider-button--${direction}`)?.click();
});

class OlyndraBundle extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.sectionId = this.dataset.sectionId;
    this.productForm = null;
    this.selectedQuantity = 1;
    this.productInfo = null;
    this.variantChangeUnsubscriber = null;
    this.variantSyncTimer = null;
    this.handleProductChange = this.handleProductChange.bind(this);
  }

  connectedCallback() {
    const json = this.querySelector('script[type="application/json"]');
    try {
      this.variants = JSON.parse(json?.textContent || '[]');
    } catch (error) {
      console.error('Unable to read product variants for the bundle selector.', error);
    }

    this.productForm = document.querySelector(`#product-form-${CSS.escape(this.sectionId)}`);
    this.productInfo = this.closest('product-info') || this.productForm?.closest('product-info');
    this.querySelectorAll('[data-bundle-offer]').forEach((offer) => {
      offer.addEventListener('click', (event) => {
        if (event.target.closest('select')) return;
        this.selectOffer(offer);
      });
    });
    this.querySelectorAll('select').forEach((select) => {
      select.addEventListener('change', () => {
        this.refreshRowAvailability(select.closest('[data-bundle-row]'));
        this.validateRows();
        this.dispatchBundleChange();
      });
    });

    const initial = this.querySelector('[data-bundle-offer].is-selected') || this.querySelector('[data-bundle-offer]');
    if (initial) this.selectOffer(initial);

    const selectedVariant = this.getMainVariant();
    if (selectedVariant) this.syncRowsToVariant(selectedVariant);

    this.productInfo?.addEventListener('change', this.handleProductChange);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (!data?.variant || (data.sectionId && data.sectionId !== this.sectionId)) return;
        this.syncRowsToVariant(data.variant);
      });
    }
  }

  disconnectedCallback() {
    this.productInfo?.removeEventListener('change', this.handleProductChange);
    this.variantChangeUnsubscriber?.();
    window.clearTimeout(this.variantSyncTimer);
  }

  handleProductChange(event) {
    if (this.contains(event.target)) return;
    if (!event.target.matches('[name="id"]') && !event.target.closest('variant-selects')) return;
    window.clearTimeout(this.variantSyncTimer);
    this.variantSyncTimer = window.setTimeout(() => {
      const variant = this.getMainVariant();
      if (variant) this.syncRowsToVariant(variant);
    }, 0);
  }

  selectOffer(offer) {
    this.querySelectorAll('[data-bundle-offer]').forEach((item) => item.classList.toggle('is-selected', item === offer));
    const radio = offer.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;
    this.selectedQuantity = Number(offer.dataset.quantity || 1);
    this.dataset.selectedQuantity = String(this.selectedQuantity);

    const quantityInput = this.productForm?.querySelector('[name="quantity"]');
    if (quantityInput) quantityInput.value = String(this.selectedQuantity);
    this.refreshAvailability();
    this.validateRows();
    this.dispatchBundleChange();
  }

  getSelectedOffer() {
    return this.querySelector('[data-bundle-offer].is-selected');
  }

  getMainVariant() {
    const variantId = Number(this.productForm?.querySelector('[name="id"]')?.value || 0);
    return this.variants.find((variant) => Number(variant.id) === variantId);
  }

  getSelectedRows() {
    if (this.selectedQuantity <= 1) return [];
    return Array.from(this.getSelectedOffer()?.querySelectorAll('[data-bundle-row]') || []).slice(
      0,
      this.selectedQuantity
    );
  }

  getRowOptions(row) {
    return Array.from(row?.querySelectorAll('select') || []).map((select) => select.value);
  }

  getSelectedVariants() {
    if (this.selectedQuantity <= 1) {
      const variant = this.getMainVariant();
      return variant?.available ? [variant] : [];
    }

    return this.getSelectedRows().map((row) => {
      const options = this.getRowOptions(row);
      const variant = this.variants.find((item) => {
        const itemOptions = item.options || [item.option1, item.option2, item.option3].filter(Boolean);
        return itemOptions.length === options.length && itemOptions.every((value, index) => value === options[index]);
      });
      row.classList.toggle('has-error', !variant || !variant.available);
      return variant?.available ? variant : null;
    });
  }

  getSelectedOptionGroups() {
    if (this.selectedQuantity <= 1) {
      const variant = this.getMainVariant();
      return variant ? [variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)] : [];
    }

    return this.getSelectedRows().map((row) => this.getRowOptions(row));
  }

  refreshAvailability() {
    this.querySelectorAll('[data-bundle-row]').forEach((row) => this.refreshRowAvailability(row));
  }

  refreshRowAvailability(row) {
    if (!row) return;
    const selects = Array.from(row.querySelectorAll('select'));
    const selectedOptions = selects.map((select) => select.value);

    selects.forEach((select, optionIndex) => {
      Array.from(select.options).forEach((option) => {
        const optionValue = option.value;
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = optionValue;
        const available = this.variants.some((variant) => {
          const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
          return (
            variant.available &&
            variantOptions.length === candidateOptions.length &&
            variantOptions.every((value, index) => value === candidateOptions[index])
          );
        });
        const label = option.dataset.bundleLabel || option.textContent.replace(/ — Sold out$/, '');
        option.dataset.bundleLabel = label;
        option.disabled = !available;
        option.textContent = available ? label : `${label} — Sold out`;
        option.value = optionValue;
      });
    });
  }

  syncRowsToVariant(variant) {
    const options = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
    this.querySelectorAll('[data-bundle-row]').forEach((row) => {
      row.querySelectorAll('select').forEach((select, index) => {
        const targetOption = Array.from(select.options).find((option) => option.value === options[index]);
        if (targetOption) {
          targetOption.disabled = false;
          select.value = options[index];
        }
      });
    });
    this.refreshAvailability();
    this.validateRows();
    this.dispatchBundleChange();
  }

  dispatchBundleChange() {
    this.dispatchEvent(
      new CustomEvent('olyndra:bundle-change', {
        bubbles: true,
        detail: {
          quantity: this.selectedQuantity,
          optionGroups: this.getSelectedOptionGroups(),
        },
      })
    );
  }

  getSelectedVariantIds() {
    return this.getSelectedVariants().map((variant) => Number(variant?.id || 0));
  }

  validateRows() {
    return this.getSelectedVariantIds().every(Boolean);
  }
}

if (!customElements.get('olyndra-bundle')) {
  customElements.define('olyndra-bundle', OlyndraBundle);
}

async function addOlyndraBundle(event, bundle) {
  if (!bundle || Number(bundle.dataset.selectedQuantity || 1) <= 1) return false;

  const form = event.target;
  const submitButton = form.querySelector('[type="submit"]');
  const spinner = submitButton?.querySelector('.loading__spinner');
  const variantIds = bundle.getSelectedVariantIds();

  if (!variantIds.length || variantIds.some((id) => !id)) {
    const error = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const message = error?.querySelector('.product-form__error-message');
    if (message) message.textContent = 'Please choose an available option for every piece.';
    if (error) error.hidden = false;
    return true;
  }

  submitButton?.setAttribute('aria-disabled', 'true');
  submitButton?.classList.add('loading');
  spinner?.classList.remove('hidden');

  const quantities = new Map();
  variantIds.forEach((id) => quantities.set(id, (quantities.get(id) || 0) + 1));
  const items = Array.from(quantities, ([id, quantity]) => ({
    id,
    quantity,
    properties: { _bundle_quantity: String(variantIds.length) },
  }));

  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const payload = { items };
  if (cart?.getSectionsToRender) {
    payload.sections = cart.getSectionsToRender().map((section) => section.id);
    payload.sections_url = window.location.pathname;
    cart.setActiveElement?.(document.activeElement);
  }

  try {
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add the bundle.');

    if (cart?.renderContents) {
      cart.renderContents(data);
      cart.classList.remove('is-empty');
    } else {
      window.location.assign(window.routes?.cart_url || '/cart');
    }

  } catch (error) {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const message = wrapper?.querySelector('.product-form__error-message');
    if (message) message.textContent = error.message;
    if (wrapper) wrapper.hidden = false;
  } finally {
    submitButton?.removeAttribute('aria-disabled');
    submitButton?.classList.remove('loading');
    spinner?.classList.add('hidden');
  }

  return true;
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    const productInfo = form.closest('product-info');
    const bundle = productInfo?.querySelector('olyndra-bundle');
    if (!bundle || Number(bundle.dataset.selectedQuantity || 1) <= 1) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addOlyndraBundle(event, bundle);
  },
  true
);

class OlyndraStickyAtc extends HTMLElement {
  constructor() {
    super();
    this.mainButton = null;
    this.stickyButton = null;
    this.variantChangeUnsubscriber = null;
    this.scrollTicking = false;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleProductChange = this.handleProductChange.bind(this);
    this.submitSelection = this.submitSelection.bind(this);
  }

  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    const configuredButton = sectionId
      ? document.querySelector(`#ProductSubmitButton-${CSS.escape(sectionId)}`)
      : null;
    const mainProduct = document.querySelector('product-info[id^="MainProduct-"]');
    this.mainButton = configuredButton || mainProduct?.querySelector('.product-form__submit');
    if (!this.mainButton) return;

    this.productInfo = this.mainButton.closest('product-info');
    this.stickyButton = this.querySelector('[data-sticky-submit]');

    this.designMode = Boolean(window.Shopify?.designMode);
    this.classList.toggle('is-theme-editor-preview', this.designMode);

    this.stickyButton?.addEventListener('click', this.submitSelection);
    document.addEventListener('change', this.handleProductChange);
    document.addEventListener('variant:change', this.handleProductChange);
    this.productInfo?.addEventListener('olyndra:bundle-change', this.handleProductChange);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (!data?.sectionId || data.sectionId === sectionId) this.update();
      });
    }
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleScroll, { passive: true });
    this.update();
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.stickyButton?.removeEventListener('click', this.submitSelection);
    document.removeEventListener('change', this.handleProductChange);
    document.removeEventListener('variant:change', this.handleProductChange);
    this.productInfo?.removeEventListener('olyndra:bundle-change', this.handleProductChange);
    this.variantChangeUnsubscriber?.();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  }

  handleProductChange(event) {
    if (event.type === 'variant:change' || event.type === 'olyndra:bundle-change' || this.productInfo?.contains(event.target)) {
      this.update();
    }
  }

  submitSelection() {
    // Verplaats focus eerst naar de echte add-to-cart-knop, zodat DIE (en niet
    // de vaste sticky-knop) wordt opgeslagen als het element om na sluiten van
    // de cart weer te focussen. Voorkomt dat de pagina terugspringt/vastloopt
    // wanneer de cart-drawer/notification wordt gesloten.
    this.mainButton?.focus({ preventScroll: true });

    const bundle = this.productInfo?.querySelector('olyndra-bundle');
    const quantity = Number(bundle?.dataset.selectedQuantity || 1);
    if (!bundle || quantity <= 1) {
      this.mainButton?.click();
      return;
    }

    if (!bundle.validateRows()) {
      this.update();
      return;
    }

    const form = bundle.productForm || this.mainButton?.closest('form[data-type="add-to-cart-form"]');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  handleScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    window.requestAnimationFrame(() => {
      this.updateVisibility();
      this.scrollTicking = false;
    });
  }

  updateVisibility() {
    const threshold = Math.min(90, Math.max(0, Number(this.dataset.showAfterPercent) || 0));
    const scrollableHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const scrollPercent = (window.scrollY / scrollableHeight) * 100;
    this.classList.toggle('is-visible', this.designMode || scrollPercent >= threshold);
  }

  update() {
    const productInfo = this.productInfo || this.mainButton?.closest('product-info');
    const bundle = productInfo?.querySelector('olyndra-bundle');
    const quantity = Number(bundle?.dataset.selectedQuantity || 1);
    const optionGroups = bundle?.getSelectedOptionGroups() || [];
    const selected = optionGroups.length
      ? optionGroups.map((options) => options.filter(Boolean).join(' / ')).join(' • ')
      : Array.from(productInfo?.querySelectorAll('.product-form__input input[type="radio"]:checked') || [])
          .map((input) => input.value)
          .filter(Boolean)
          .join(' / ');
    const variant = this.querySelector('[data-sticky-variant]');
    if (variant) variant.textContent = selected;

    const priceTarget = this.querySelector('[data-sticky-prices]');
    const selectedOffer = bundle?.getSelectedOffer();
    if (quantity > 1 && selectedOffer && priceTarget) {
      const current = selectedOffer.querySelector('.ol-bundle__price-current')?.textContent?.trim() || '';
      const compare = selectedOffer.querySelector('.ol-bundle__price-compare')?.textContent?.trim() || '';
      priceTarget.innerHTML = `
        <div class="price price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span class="price-item price-item--sale price-item--last">${current}</span>
              <s class="price-item price-item--regular">${compare}</s>
            </div>
          </div>
        </div>
      `;
    } else {
      const priceSource = productInfo?.querySelector('[id^="price-"] .price');
      if (priceSource && priceTarget) {
        priceTarget.innerHTML = priceSource.outerHTML;
        priceTarget.querySelectorAll('.price__badge-sale, .price__badge-sold-out').forEach((badge) => badge.remove());
      }
    }

    if (this.stickyButton) {
      this.stickyButton.disabled = quantity > 1 ? !bundle?.validateRows() : Boolean(this.mainButton?.disabled);
    }
  }
}

if (!customElements.get('olyndra-sticky-atc')) {
  customElements.define('olyndra-sticky-atc', OlyndraStickyAtc);
}

class OlyndraReviewSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-review-track]');
    if (!this.track) return;

    this.querySelector('[data-review-previous]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-review-next]')?.addEventListener('click', () => this.move(1));
  }

  move(direction) {
    const card = this.track.querySelector('.ol-review-card');
    if (!card) return;
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || 0);
    const distance = card.getBoundingClientRect().width + gap;
    this.track.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }
}

if (!customElements.get('olyndra-review-slider')) {
  customElements.define('olyndra-review-slider', OlyndraReviewSlider);
}

function initializeOlyndraDescriptionImages(root = document) {
  root.querySelectorAll('.product__description img').forEach((image) => {
    if (image.dataset.olyndraImageRepairReady === 'true') return;
    image.dataset.olyndraImageRepairReady = 'true';

    const repairImage = () => {
      const description = image.closest('.product__description');
      const fallback = description?.dataset.descriptionFallback;

      if (fallback && image.dataset.olyndraFallbackApplied !== 'true') {
        image.dataset.olyndraFallbackApplied = 'true';
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = fallback;
        return;
      }

      image.classList.add('ol-description-image--unavailable');
    };

    image.addEventListener('error', repairImage);
    if (image.complete && image.naturalWidth === 0) repairImage();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeOlyndraDescriptionImages());
} else {
  initializeOlyndraDescriptionImages();
}

document.addEventListener('shopify:section:load', (event) => initializeOlyndraDescriptionImages(event.target));
